import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Badge, Button, Input } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Edit, Trash2, Key } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';

// Usernames and emails are interpolated into SweetAlert's `html`, so escape them.
const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');

  const ROLES = ['superadmin', 'admin', 'manager', 'user'];

  // This page is for staff only. Customer logins are managed from All Customers,
  // where the actions that make sense for them live (reset portal password,
  // credit notes) — listing them here as well made the same account editable in
  // two places with different rules, and buried the handful of staff accounts
  // among 46 customers.
  const staffUsers = users.filter((u) => u.role !== 'customer');

  // An admin looking someone up rarely knows which column their term sits in, so
  // match across the name columns, the email and the id together.
  const query = search.trim().toLowerCase();
  const matchesSearch = (u) =>
    !query ||
    [u.username, u.firstname, u.lastname, u.email, String(u.id)]
      .some((field) => String(field ?? '').toLowerCase().includes(query));

  const filteredUsers = staffUsers.filter(
    (u) => (roleFilter === 'all' || u.role === roleFilter) && matchesSearch(u)
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  // The /users endpoints require a staff token; an expired one must send the
  // admin back to login rather than leave a page that silently fails to load.
  const handleAuthError = (error) => {
    if ([401, 403].includes(error.response?.status)) {
      toast.error('Your session has expired. Please sign in again.');
      navigate('/login');
      return true;
    }
    return false;
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`, authHeader());
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
      if (handleAuthError(error)) return;
      toast.error('Failed to fetch users');
    }
  };

  const handleResetPassword = async (user) => {
    const canEmail = Boolean(user.email);
    const confirm = await Swal.fire({
      title: 'Reset password?',
      html: `Generate a new temporary password for <strong>${escapeHtml(user.username || user.email)}</strong>.
        The current password will stop working.<br/><br/>
        ${canEmail
          ? `Choose <strong>Reset &amp; send email</strong> and an email will be sent to
             <strong>${escapeHtml(user.email)}</strong> with the login details.`
          : `<em>This user has no email address, so the login details can't be emailed.</em>`}`,
      icon: 'question',
      showCancelButton: true,
      showDenyButton: canEmail,
      confirmButtonText: canEmail ? 'Reset & send email' : 'Reset',
      denyButtonText: 'Reset without email',
      cancelButtonText: 'Cancel',
    });
    // isConfirmed = reset + email, isDenied = reset only, anything else = cancelled.
    if (!confirm.isConfirmed && !confirm.isDenied) return;
    const sendEmail = canEmail && confirm.isConfirmed;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/users/${user.id}/reset-password`, { sendEmail }, authHeader());
      const { email, tempPassword, emailSent, emailError } = response.data;
      const credentials = `<div style="text-align:left">
        <strong>Email:</strong> ${escapeHtml(email || '-')}<br/>
        <strong>Temp password:</strong> <code style="font-size:16px">${escapeHtml(tempPassword)}</code><br/><br/>
        <small>This password is shown only once.</small></div>`;

      if (sendEmail && emailSent) {
        await Swal.fire({
          title: 'Password reset & email sent',
          html: `<div style="text-align:left">The login details were emailed to
            <strong>${escapeHtml(email)}</strong>.<br/><br/></div>${credentials}`,
          icon: 'success',
        });
      } else if (sendEmail && !emailSent) {
        // The reset still happened — say so plainly, and keep the password visible.
        await Swal.fire({
          title: 'Password reset, but the email failed',
          html: `<div style="text-align:left">The password was reset, but the email to
            <strong>${escapeHtml(email)}</strong> could not be sent${emailError ? `: ${escapeHtml(emailError)}` : ''}.
            Share the details below manually.<br/><br/></div>${credentials}`,
          icon: 'warning',
        });
      } else {
        await Swal.fire({
          title: 'Password reset',
          html: `<div style="text-align:left">Share these credentials with the user:<br/><br/></div>${credentials}`,
          icon: 'success',
        });
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      // 503 means the server refused *before* changing anything — say so, or the
      // admin is left guessing whether the password still works.
      const refusedSafely = error.response?.status === 503;
      Swal.fire({
        title: refusedSafely ? 'Email unavailable — password unchanged' : 'Failed to reset password',
        text: error.response?.data?.error || error.message,
        icon: refusedSafely ? 'warning' : 'error',
      });
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/users/${userId}`, authHeader());
      toast.success('User deleted successfully');
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting user:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'danger';
      case 'admin':
        return 'warning';
      case 'manager':
        return 'info';
      case 'user':
        return 'secondary';
      case 'customer':
        return 'success';
      default:
        return 'secondary';
    }
  };

  return (
    <>
      <Breadcrumbs parent="Users" title="User List" mainTitle="User List" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5>Staff Users</h5>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name or email"
                    style={{ width: 'auto', minWidth: '220px' }}
                    aria-label="Search users"
                  />
                  <Input
                    type="select"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{ width: 'auto' }}
                    aria-label="Filter by role"
                  >
                    <option value="all">All roles ({staffUsers.filter(matchesSearch).length})</option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)} (
                        {staffUsers.filter((u) => u.role === r && matchesSearch(u)).length})
                      </option>
                    ))}
                  </Input>
                  <Button color="primary" onClick={() => navigate(`${process.env.PUBLIC_URL}/add-user`)}>
                    Add New User
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center">Loading...</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Username</th>
                          <th>First Name</th>
                          <th>Last Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center">
                              {query || roleFilter !== 'all'
                                ? 'No users match this search.'
                                : 'No users found'}
                            </td>
                          </tr>
                        ) : (
                          filteredUsers.map((user) => (
                            <tr key={user.id}>
                              <td>{user.id}</td>
                              <td>{user.username}</td>
                              <td>{user.firstname || '-'}</td>
                              <td>{user.lastname || '-'}</td>
                              <td>{user.email}</td>
                              <td>
                                <Badge color={getRoleBadgeColor(user.role)} pill>
                                  {user.role}
                                </Badge>
                              </td>
                              <td>
                                {user.created_at
                                  ? new Date(user.created_at).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => navigate(`/edit-user/${user.id}`)}
                                  title="Edit User"
                                >
                                  <Edit size={14} />
                                </Button>
                                {' '}
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => handleResetPassword(user)}
                                  title="Reset password"
                                >
                                  <Key size={14} />
                                </Button>
                                {' '}
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(user.id)}
                                  title="Delete User"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UserList;
